// Camera Screen - Enhanced with Upload + Improved Layout
import { useState, useRef, useEffect } from 'react';
import { useApp } from '../hooks/useApp';
import { SCREENS, CAMERA_STEPS } from '../config/constants';
import Logger from '../services/logger';
import ImageQualityService from '../services/ImageQualityService';
import { Container, Text, Button, IconButton, QualityIndicator } from '../components/ui';
import Icons from '../components/Icons';

function CameraScreen() {
    const { setCurrentScreen, goBack, analysisType, capturedImages, setCapturedImages } = useApp();
    const videoRef = useRef(null);
    const fileInputRef = useRef(null);
    const [stream, setStream] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [facingMode, setFacingMode] = useState('user');
    const [isCapturing, setIsCapturing] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [quality, setQuality] = useState(0);
    const [qualityAnalysis, setQualityAnalysis] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [cameraError, setCameraError] = useState(false);

    const steps = CAMERA_STEPS[analysisType];
    const currentStepData = steps[currentStep];

    // Start camera
    useEffect(() => {
        const startCamera = async () => {
            try {
                Logger.info('CAMERA', 'Starting camera', { facingMode });
                const mediaStream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
                    audio: false,
                });
                setStream(mediaStream);
                setCameraError(false);
                if (videoRef.current) {
                    videoRef.current.srcObject = mediaStream;
                }
            } catch (error) {
                Logger.error('CAMERA', 'Failed to start camera', { error: error.message });
                setCameraError(true);
            }
        };

        startCamera();

        return () => {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, [facingMode]);

    // Real-time quality monitoring (analyze every 1 second)
    useEffect(() => {
        if (!videoRef.current || showPreview || cameraError) return;

        const analyzeInterval = setInterval(async () => {
            const video = videoRef.current;
            if (video && video.videoWidth > 0 && video.videoHeight > 0 && !isAnalyzing) {
                setIsAnalyzing(true);
                try {
                    const analysis = await ImageQualityService.analyzeQuality(video);
                    setQualityAnalysis(analysis);
                    setQuality(analysis.overallScore);
                } catch (error) {
                    if (!error.message.includes('invalid dimensions')) {
                        Logger.error('CAMERA', 'Quality analysis failed', { error: error.message });
                    }
                } finally {
                    setIsAnalyzing(false);
                }
            }
        }, 1000);

        return () => clearInterval(analyzeInterval);
    }, [showPreview, isAnalyzing, cameraError]);

    // Handle file upload
    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        Logger.info('CAMERA', 'Uploading image file', { name: file.name, type: file.type });

        const reader = new FileReader();
        reader.onload = async (event) => {
            const imageData = event.target.result;

            // Analyze uploaded image quality
            const img = new Image();
            img.onload = async () => {
                try {
                    const analysis = await ImageQualityService.analyzeQuality(img);
                    setQuality(analysis.overallScore);
                    setQualityAnalysis(analysis);
                } catch {
                    setQuality(80); // Default quality for uploads
                    setQualityAnalysis({ issues: [], suggestions: [], overallScore: 80 });
                }

                setCapturedImages({ ...capturedImages, [currentStepData.type]: imageData });
                setShowPreview(true);
            };
            img.src = imageData;
        };
        reader.readAsDataURL(file);

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const captureImage = async () => {
        if (!videoRef.current || isCapturing) return;

        setIsCapturing(true);
        Logger.info('CAMERA', 'Capturing image', { step: currentStepData.type });

        const canvas = document.createElement('canvas');
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(videoRef.current, 0, 0);
        const imageData = canvas.toDataURL('image/jpeg', 0.9);

        const analysis = await ImageQualityService.analyzeQuality(videoRef.current);
        setQuality(analysis.overallScore);
        setQualityAnalysis(analysis);

        setCapturedImages({ ...capturedImages, [currentStepData.type]: imageData });
        setShowPreview(true);
        setIsCapturing(false);
        Logger.debug('CAMERA', 'Image captured', { quality: analysis.overallScore });
    };

    const confirmCapture = () => {
        setShowPreview(false);
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            Logger.info('CAMERA', 'All images captured, proceeding to questionnaire');
            setCurrentScreen(SCREENS.QUESTIONNAIRE);
        }
    };

    const retakePhoto = () => {
        setShowPreview(false);
        setQuality(0);
    };

    const toggleCamera = () => {
        setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    };

    return (
        <Container className="min-h-screen bg-theme-bg flex flex-col" safe>
            {/* Header */}
            <div className="flex items-center justify-between px-responsive py-4 z-20">
                <IconButton onClick={goBack}><Icons.Close /></IconButton>
                <div className="text-center">
                    <Text className="font-semibold">{currentStepData.title}</Text>
                    <Text variant="small" className="text-theme-secondary">Bước {currentStep + 1}/{steps.length}</Text>
                </div>
                <IconButton onClick={toggleCamera}><Icons.Flip /></IconButton>
            </div>

            {/* Camera view */}
            <div className="flex-1 relative flex items-center justify-center overflow-hidden bg-black">
                {!cameraError ? (
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
                    />
                ) : (
                    /* Fallback for desktop without camera */
                    <div className="flex flex-col items-center justify-center text-white p-8 text-center">
                        <div className="text-5xl mb-4">📷</div>
                        <Text className="text-white mb-2">Không thể truy cập camera</Text>
                        <Text variant="small" className="text-white/70 mb-4">
                            Hãy sử dụng nút Upload bên dưới để tải ảnh lên
                        </Text>
                    </div>
                )}

                {/* Frame guide */}
                {!cameraError && !showPreview && (
                    <div className={`relative z-10 ${currentStepData.frameType === 'circle' ? 'w-64 h-64 rounded-full' : 'w-48 h-64 rounded-3xl'} border-4 border-primary/80`}>
                        <div className="absolute inset-0 flex items-center justify-center">
                            {currentStepData.frameType === 'oval' && (
                                <Icons.Tongue />
                            )}
                        </div>
                    </div>
                )}

                {/* Preview overlay */}
                {showPreview && capturedImages[currentStepData.type] && (
                    <div className="absolute inset-0 z-20 bg-black/90 flex flex-col items-center justify-center p-6">
                        <img
                            src={capturedImages[currentStepData.type]}
                            alt="Preview"
                            className="max-h-[50vh] rounded-2xl mb-4"
                            style={{ transform: facingMode === 'user' && !cameraError ? 'scaleX(-1)' : 'none' }}
                        />
                        <QualityIndicator quality={quality} />
                        <div className="flex gap-4 mt-6">
                            <Button variant="secondary" onClick={retakePhoto}>Chụp lại</Button>
                            <Button onClick={confirmCapture}>
                                {currentStep < steps.length - 1 ? 'Tiếp tục' : 'Phân tích'}
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Quality Feedback + Instructions - BELOW camera, ABOVE button */}
            {!showPreview && (
                <div className="px-responsive py-3 bg-theme-secondary border-t border-theme">
                    {/* Quality Score */}
                    {qualityAnalysis && !cameraError && (
                        <div className="flex items-center justify-between mb-2">
                            <Text variant="small" className="font-medium">Chất lượng ảnh</Text>
                            <div className="flex items-center gap-2">
                                <div className="w-20 h-2 rounded-full bg-theme-tertiary overflow-hidden">
                                    <div
                                        className="h-full transition-all duration-300 rounded-full"
                                        style={{
                                            width: `${quality}%`,
                                            backgroundColor: ImageQualityService.getQualityColor(quality)
                                        }}
                                    />
                                </div>
                                <Text
                                    variant="small"
                                    className="font-bold w-8 text-right"
                                    style={{ color: ImageQualityService.getQualityColor(quality) }}
                                >
                                    {quality}
                                </Text>
                            </div>
                        </div>
                    )}

                    {/* Issues & Suggestions */}
                    {qualityAnalysis && !cameraError && (
                        <div className="space-y-1 mb-2">
                            {qualityAnalysis.issues.slice(0, 1).map((issue, idx) => (
                                <Text key={idx} variant="caption" className="text-warning">
                                    ⚠ {issue}
                                </Text>
                            ))}
                            {qualityAnalysis.suggestions.length > 0 && quality < 80 && (
                                <Text variant="caption" className="text-theme-secondary">
                                    💡 {qualityAnalysis.suggestions[0]}
                                </Text>
                            )}
                            {quality >= 80 && (
                                <Text variant="caption" className="text-success">
                                    ✓ Sẵn sàng chụp!
                                </Text>
                            )}
                        </div>
                    )}

                    {/* Instruction */}
                    <Text variant="caption" className="text-theme-tertiary text-center block">
                        {currentStepData.desc}
                    </Text>
                </div>
            )}

            {/* Capture/Upload buttons - Light mode improved */}
            {!showPreview && (
                <div className="flex items-center justify-center gap-6 py-6 px-responsive bg-theme-bg">
                    {/* Upload button */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="image-upload"
                    />
                    <label
                        htmlFor="image-upload"
                        className="w-14 h-14 rounded-full bg-theme-secondary border-2 border-theme flex items-center justify-center cursor-pointer hover:bg-theme-tertiary transition-colors"
                    >
                        <Icons.Upload />
                    </label>

                    {/* Capture button - Improved for light mode */}
                    <button
                        onClick={captureImage}
                        disabled={isCapturing || cameraError}
                        className={`
                            w-20 h-20 sm:w-24 sm:h-24 rounded-full 
                            border-4 flex items-center justify-center 
                            active:scale-95 transition-all touch-target
                            ${cameraError
                                ? 'border-gray-400 opacity-50 cursor-not-allowed'
                                : 'border-primary dark:border-white hover:border-primary/80 dark:hover:border-white/80'
                            }
                        `}
                    >
                        <div className={`
                            w-14 h-14 sm:w-16 sm:h-16 rounded-full 
                            ${cameraError
                                ? 'bg-gray-400'
                                : 'bg-primary dark:bg-white'
                            }
                        `} />
                    </button>

                    {/* Spacer for symmetry */}
                    <div className="w-14 h-14" />
                </div>
            )}

            {/* Hidden file input for upload */}
        </Container>
    );
}

export default CameraScreen;
